import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BoltOutlined, DonutLargeOutlined, GroupAddOutlined, TrendingDownOutlined, TrendingFlatOutlined, TrendingUpOutlined } from '@mui/icons-material'
import { AvatarGroup, Box, Card, CardContent, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'

import { densityScalarOf } from '../../../../api/blueprintFamilies/trendsBatch'
import { BlueprintFamilyTrendsMonthlyPoint } from '../../../../api/core'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { borderRadiusInset } from '../../../../themes/theme'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { NewAdopterGameIcon } from '../NewAdopterGameIcon/NewAdopterGameIcon'
import { StatValueCard } from '../StatValueCard/StatValueCard'
import { WindowTooltipContent } from '../WindowTooltipContent/WindowTooltipContent'
import { adoptionChangeHover, changeBaselineLabel, changeLatestLabel } from '../helpers/adoptionChangeCopy'
import {
  adoptionChangePp,
  CATALOGUE_WINDOWS,
  CatalogueWindow,
  catalogueWindowMonths,
  formatAdoptionDelta,
  formatAdoptionPct,
  formatDensity,
  formatSignedInt,
} from '../helpers/catalogueUtils'
import { deltaDirectionArrow } from '../helpers/deltaArrow'
import { NewAdopterGame } from '../helpers/newAdopterGames'
import { renderStatTileValue, STAT_TILE_SKELETON } from '../helpers/statTiles'
import { compactToggleGroupSx } from '../helpers/toggleStyles'
import { sectionLabelSx } from '../helpers/typographyStyles'

// Which taxonomy level the tiles describe. Same four numbers at every level;
// only the section title and the tooltip wording change.
export type LevelStatsKind = 'blueprint' | 'eventType' | 'eventCategory'

export interface LevelStatsCardProps {
  kind: LevelStatsKind
  window: CatalogueWindow
  onWindowChange: (next: CatalogueWindow) => void
  // Cohort games active during the period's last month (list `distinctGamesCount` or
  // the rollup row's), deduplicated per game.
  adoptingGames: number | null
  // The cohort behind BOTH tiles: games tracked on or before the period's first day
  // (`cohortGamesCount`). Adoption and Change used to
  // divide by different populations; since the backend put them on one cohort they
  // share this denominator, so a Change of +N% is directly readable against Adoption.
  cohortSize: number | null
  // Whole-market windowed adoption rate: fallback when the cohort size is unknown.
  adoptionRateFallback?: number
  // Net change in adopting games over the window, from the momentum endpoint
  // (`groupBy=FAMILY` for a family, `EVENT_GROUP` / `EVENT_TYPE` for a level). Null =
  // unknown; the tile shows a dash and `changeUnavailableReason` on hover.
  deltaGames: number | null
  changeUnavailableReason?: string
  // Per-adopting-game density of the period's last month (the last point of the
  // `density` series, see `densityScalarOf`), not a window-wide figure.
  densityScalar: number | null
  densityUnavailableReason?: string
  // Games whose FIRST event at this level fell inside the window
  // (`newGames.length`) — the absolute size of the recent adoption wave, where the
  // Change tile beside it is the net move across the period.
  newAdopters: number | null
  // The games behind that count, where the level can name them (only the
  // blueprint family page can today). Their faces replace the tile's background
  // icon, and the count becomes this list's length so the two always agree.
  newAdopterGames?: NewAdopterGame[]
  // Opens the full list of those games. The tile only becomes clickable once there
  // are games to list.
  onNewAdoptersClick?: () => void
  isAdoptionLoading?: boolean
  isChangeLoading?: boolean
  isDensityLoading?: boolean
  isNewAdoptersLoading?: boolean
  // The level's trend chart, rendered under the tiles inside the same card.
  chart?: React.ReactNode
}

// Sign-driven accent: green up, red down, neutral when zero or missing.
function deltaColor(theme: Theme, delta: number | null): string {
  if (delta === null || delta === 0) return theme.palette.text.secondary
  return delta > 0 ? theme.palette.success.main : theme.palette.warning.main
}

const STAT_ICON_ADOPTION = <DonutLargeOutlined />
const STAT_ICON_DENSITY = <BoltOutlined />
const STAT_ICON_NEW_ADOPTERS = <GroupAddOutlined />
const STAT_ICON_TREND_UP = <TrendingUpOutlined />
const STAT_ICON_TREND_DOWN = <TrendingDownOutlined />
const STAT_ICON_TREND_FLAT = <TrendingFlatOutlined />

function trendIcon(delta: number | null): React.ReactNode {
  if (delta === null || delta === 0) return STAT_ICON_TREND_FLAT
  return delta > 0 ? STAT_ICON_TREND_UP : STAT_ICON_TREND_DOWN
}

// How many faces fit beside the count before the rest collapse into "+N".
const NEW_ADOPTER_AVATARS_SHOWN = 4
const NEW_ADOPTER_AVATAR_SIZE = 26
const NEW_ADOPTER_AVATAR_OVERLAP = 10
// Larger than the faces: the platform badge plate has a 14px floor, which would swallow a 22px icon.
const NEW_ADOPTER_TOOLTIP_AVATAR_SIZE = 28
const NEW_ADOPTER_SURPLUS_CLASS = 'NewAdopterAvatars-surplus'
// AvatarGroup rings every avatar with a 2px border outside its box.
const NEW_ADOPTER_AVATAR_OUTER = NEW_ADOPTER_AVATAR_SIZE + 2 * 2
const NEW_ADOPTER_AVATAR_STEP = NEW_ADOPTER_AVATAR_OUTER - NEW_ADOPTER_AVATAR_OVERLAP
// Room the "+N" chip takes: ~7px a character at its 11px bold, its 6px side padding,
// the ring and its 4px gap from the stack. Errs wide; the stack clips if it's off.
const surplusChipWidth = (surplus: number): number => Math.max(NEW_ADOPTER_AVATAR_SIZE, `+${surplus}`.length * 7 + 12) + 2 * 2 + 4

const facesFitting = (room: number): number =>
  room < NEW_ADOPTER_AVATAR_OUTER ? 0 : 1 + Math.floor((room - NEW_ADOPTER_AVATAR_OUTER) / NEW_ADOPTER_AVATAR_STEP)

/**
 * How many faces to show in `width` px: every game when they all fit, otherwise as
 * many as fit beside the "+N" chip (at least one — AvatarGroup won't show fewer).
 * Unmeasured (first paint, no ResizeObserver) it shows the usual maximum.
 */
function visibleAdopterFaces(total: number, width: number | null): number {
  if (!width) return Math.min(total, NEW_ADOPTER_AVATARS_SHOWN)
  if (total <= NEW_ADOPTER_AVATARS_SHOWN && facesFitting(width) >= total) return total
  return Math.max(1, Math.min(NEW_ADOPTER_AVATARS_SHOWN, facesFitting(width - surplusChipWidth(total - 1))))
}

/**
 * The new adopters as game icons beside the count, with every name on hover —
 * the surplus avatar ("+3") is the only place the rest are reachable, so the
 * tooltip lists all of them rather than only the hidden ones.
 */
const NewAdopterAvatars: React.FC<{ games: NewAdopterGame[] }> = ({ games }) => {
  // Takes whatever the count leaves of the tile's width, so narrow tiles show
  // fewer faces and a bigger "+N" instead of pushing the chip out of view.
  const slotRef = useRef<HTMLDivElement>(null)
  const [slotWidth, setSlotWidth] = useState<number | null>(null)

  useEffect(() => {
    const slot = slotRef.current
    if (!slot || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => setSlotWidth(entry.contentRect.width))
    observer.observe(slot)
    return () => observer.disconnect()
  }, [])

  const shown = visibleAdopterFaces(games.length, slotWidth)

  return (
    <Box ref={slotRef} sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
      <GRTooltip
        content={
          <Stack component="ul" spacing={0.75} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {games.map((game) => (
              <Stack key={game.gameId} component="li" direction="row" alignItems="center" spacing={1}>
                <NewAdopterGameIcon
                  game={game}
                  size={NEW_ADOPTER_TOOLTIP_AVATAR_SIZE}
                  decorative
                  sx={{ width: NEW_ADOPTER_TOOLTIP_AVATAR_SIZE, height: NEW_ADOPTER_TOOLTIP_AVATAR_SIZE, borderRadius: '7px' }}
                />
                <Typography variant="body2">{game.name}</Typography>
              </Stack>
            ))}
          </Stack>
        }
        styles={{ cursor: 'help', display: 'inline-flex', minWidth: 0 }}
      >
        <AvatarGroup
          // `max` counts the surplus avatar itself, and only slicing the children
          // would lose the total it counts against — so do both.
          max={shown + 1}
          total={games.length}
          spacing={NEW_ADOPTER_AVATAR_OVERLAP}
          variant="rounded"
          slotProps={{ additionalAvatar: { className: NEW_ADOPTER_SURPLUS_CLASS } }}
          sx={{
            // Safety net under the measured count: the group is row-reversed with the
            // chip at the right, so overflow clips the leftmost faces, never the "+N".
            minWidth: 0,
            overflow: 'hidden',
            '& .MuiAvatar-root': {
              width: NEW_ADOPTER_AVATAR_SIZE,
              height: NEW_ADOPTER_AVATAR_SIZE,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              borderRadius: borderRadiusInset,
              // The ring separating overlapped faces has to match the tile, not the page.
              borderColor: 'background.paper',
            },
            // The surplus is the group's first DOM child but sits rightmost, so any overlap
            // paints its left neighbour over the "+N". Kept clear of the stack instead,
            // and widened so multi-digit counts aren't clipped.
            [`& .${NEW_ADOPTER_SURPLUS_CLASS}`]: {
              width: 'auto',
              minWidth: NEW_ADOPTER_AVATAR_SIZE,
              px: 0.75,
              ml: 0.5,
              bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
              // `primary.dark`, not `.main`: the main purple is too light for 11px text on its own tint.
              color: 'primary.dark',
              fontSize: '0.6875rem',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            },
          }}
        >
          {games.slice(0, shown).map((game, index) => (
            // The faces are too small for the platform badge, which the hover list and the modal still show.
            // The ascending `zIndex` is kept as it was: each face paints above the one to its left.
            <NewAdopterGameIcon key={game.gameId} game={game} size={NEW_ADOPTER_AVATAR_SIZE} showPlatformBadge={false} sx={{ zIndex: index + 1 }} />
          ))}
        </AvatarGroup>
      </GRTooltip>
    </Box>
  )
}

// Per-level copy keys. The numbers are the same at every level; the wording
// names what "it" is.
const COPY: Record<LevelStatsKind, { title: string; adoption: string; density: string; newAdopters: string }> = {
  blueprint: {
    title: 'market-analytics:detail_adoption_section_title',
    adoption: 'market-analytics:detail_panel_stat_current_adoption_tooltip',
    density: 'market-analytics:detail_stat_density_tooltip',
    newAdopters: 'market-analytics:new_adopters_tooltip',
  },
  eventType: {
    title: 'market-analytics:event_type_stats_section_title',
    adoption: 'market-analytics:event_type_stat_adoption_tooltip',
    density: 'market-analytics:event_type_stat_density_tooltip',
    newAdopters: 'market-analytics:event_type_stat_new_adopters_tooltip',
  },
  eventCategory: {
    title: 'market-analytics:event_category_stats_section_title',
    adoption: 'market-analytics:event_category_stat_adoption_tooltip',
    density: 'market-analytics:event_category_stat_density_tooltip',
    newAdopters: 'market-analytics:event_category_stat_new_adopters_tooltip',
  },
}

/**
 * The stats card every Live Event Analytics detail page shares (2026-09-07
 * review: blueprint family, event type and event category pages read the same
 * way): a window toggle, the same four tiles (Adoption, Change, Density, Density
 * change) and, under them, the level's one trend chart.
 */
export const LevelStatsCard = React.memo(function LevelStatsCard({
  kind,
  window: windowValue,
  onWindowChange,
  adoptingGames,
  cohortSize,
  adoptionRateFallback,
  deltaGames,
  changeUnavailableReason,
  densityScalar,
  densityUnavailableReason,
  newAdopters,
  newAdopterGames,
  onNewAdoptersClick,
  isAdoptionLoading,
  isChangeLoading,
  isDensityLoading,
  isNewAdoptersLoading,
  chart,
}: LevelStatsCardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const userLanguage = useCurrentUserLanguage()
  const copy = COPY[kind]

  const months = catalogueWindowMonths(windowValue)
  const baselineLabel = changeBaselineLabel(windowValue, userLanguage)
  const latestLabel = changeLatestLabel(windowValue, userLanguage)
  const changeSubtext = t('market-analytics:detail_panel_stat_month_vs_month', { latest: latestLabel, baseline: baselineLabel })

  // ---- Adoption tile ---------------------------------------------------------
  const adoptionRate =
    cohortSize && cohortSize > 0 && adoptingGames !== null ? adoptingGames / cohortSize : typeof adoptionRateFallback === 'number' ? adoptionRateFallback : null
  const adoptionValueText = formatAdoptionPct(adoptionRate)
  const adoptionTrailingTexts =
    cohortSize && adoptingGames !== null
      ? [t('market-analytics:detail_stat_adoption_fraction', { adopting: adoptingGames, total: cohortSize })]
      : adoptingGames !== null
      ? [t('market-analytics:catalogue_tree_games_count', { count: adoptingGames })]
      : []

  // ---- Change tile -------------------------------------------------------------
  // Percentage-point change of the adoption share over the same cohort the Adoption
  // tile divides by, from the period's first month to its last — the adoption chart's
  // first and last points. Both tiles read against one
  // population, so "+4%" here is directly comparable to the Adoption figure; the
  // label's hover names the cohort.
  const deltaPp = adoptionChangePp(deltaGames, cohortSize ?? null)
  const deltaAdoptionValueText =
    deltaGames === null
      ? '—'
      : deltaPp !== null
      ? formatAdoptionDelta(deltaPp)
      : `${formatSignedInt(deltaGames)} ${t('market-analytics:catalogue_change_games_unit')}`
  // A dash on its own reads as missing data with no explanation — the reason
  // only surfaced on the label's info icon, which is exactly the question the
  // 2026-09-08 review asked ("Event type data is not visible for some reason?").
  // The short caption says so on the tile; the full explanation stays in the hover.
  const unavailableCaption = t('market-analytics:stat_unavailable_short')
  const deltaAdoptionTrailingTexts = deltaGames === null ? (changeUnavailableReason ? [unavailableCaption] : []) : [changeSubtext]
  // Same copy as the ranked list's Change column, built once so the two surfaces
  // can't disagree about the number they both show.
  const deltaAdoptionTooltip = adoptionChangeHover({ deltaGames, baselineLabel, latestLabel, subject: kind, unavailableReason: changeUnavailableReason }, t)

  // The density scalar is the mean of the period's last month, not of the whole
  // period, so a 24mo selection would otherwise read as a two-year average.
  const densityTooltip =
    densityScalar === null && densityUnavailableReason
      ? densityUnavailableReason
      : `${t(copy.density)} ${t('market-analytics:detail_stat_density_last_month_note')}`

  // ---- New adopters tile --------------------------------------------------------
  // Where the games are known, they ARE the count: the backend figure and this list
  // are built from different endpoints, and a "2" above three faces would be worse
  // than either number alone. Levels that can't name their games keep the backend's.
  // Naming none of them is not evidence there are none — the two come from
  // different endpoints, so an empty list falls back to the backend figure
  // rather than contradicting it with a 0.
  const hasAdopterFaces = Boolean(newAdopterGames && newAdopterGames.length > 0)
  const newAdopterCount = hasAdopterFaces ? (newAdopterGames as NewAdopterGame[]).length : newAdopters
  const canOpenNewAdopters = Boolean(onNewAdoptersClick) && hasAdopterFaces && !isNewAdoptersLoading

  return (
    <Card>
      <CardContent>
        {/* Header row: section label + window toggle */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
          <Typography sx={sectionLabelSx}>{t(copy.title, { window: t('market-analytics:catalogue_window_months', { months }) })}</Typography>
          <GRTooltip content={<WindowTooltipContent />} styles={{ display: 'inline-flex' }}>
            <ToggleButtonGroup
              value={windowValue}
              exclusive
              size="small"
              onChange={(_, next) => next && onWindowChange(next)}
              aria-label={t('market-analytics:catalogue_window_label')}
              sx={compactToggleGroupSx}
            >
              {CATALOGUE_WINDOWS.map((win) => (
                <ToggleButton key={win} value={win}>
                  {t('market-analytics:catalogue_window_months', { months: catalogueWindowMonths(win) })}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </GRTooltip>
        </Stack>

        {/* Stat tiles: 2×2 on narrow screens, one row on wide */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <StatValueCard
            label={t('market-analytics:detail_stat_adoption_label')}
            labelTooltip={t(copy.adoption)}
            accentColor={theme.palette.primary.main}
            backgroundIcon={STAT_ICON_ADOPTION}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {isAdoptionLoading ? STAT_TILE_SKELETON : renderStatTileValue(theme.palette.primary.main, adoptionValueText, ...adoptionTrailingTexts)}
          </StatValueCard>

          <StatValueCard
            label={t('market-analytics:detail_stat_delta_adoption_label')}
            labelTooltip={deltaAdoptionTooltip}
            accentColor={isChangeLoading ? theme.palette.text.secondary : deltaColor(theme, deltaGames)}
            backgroundIcon={isChangeLoading ? undefined : trendIcon(deltaGames)}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {isChangeLoading
              ? STAT_TILE_SKELETON
              : renderStatTileValue(
                  deltaColor(theme, deltaGames),
                  <>
                    {deltaDirectionArrow(deltaGames, deltaColor(theme, deltaGames))}
                    {deltaAdoptionValueText}
                  </>,
                  ...deltaAdoptionTrailingTexts
                )}
          </StatValueCard>

          {/* Adoption, its change, then the size of the recent wave: the three
              game-count tiles sit together, and Density — a different measure
              entirely — closes the row. */}
          <StatValueCard
            label={t('market-analytics:new_adopters')}
            labelTooltip={t(copy.newAdopters, { months })}
            accentColor={theme.palette.success.main}
            // The faces take the corner the background icon would sit in.
            backgroundIcon={hasAdopterFaces ? undefined : STAT_ICON_NEW_ADOPTERS}
            backgroundIconPlacement="contained"
            size="dense"
            onClick={canOpenNewAdopters ? onNewAdoptersClick : undefined}
          >
            {isNewAdoptersLoading ? (
              STAT_TILE_SKELETON
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '1.65rem' },
                      lineHeight: 1.05,
                      color: theme.palette.success.main,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {newAdopterCount === null ? '—' : newAdopterCount.toLocaleString()}
                  </Typography>
                  {hasAdopterFaces ? <NewAdopterAvatars games={newAdopterGames as NewAdopterGame[]} /> : null}
                </Box>
                {newAdopterCount === null ? null : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                    {t('market-analytics:event_type_stat_new_adopters_caption', { months })}
                  </Typography>
                )}
              </>
            )}
          </StatValueCard>

          <StatValueCard
            label={t('market-analytics:detail_stat_density_label')}
            labelTooltip={densityTooltip}
            accentColor={theme.palette.secondary.main}
            backgroundIcon={STAT_ICON_DENSITY}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {isDensityLoading
              ? STAT_TILE_SKELETON
              : renderStatTileValue(
                  theme.palette.secondary.main,
                  formatDensity(densityScalar),
                  densityScalar !== null ? t('market-analytics:detail_panel_stat_density_unit') : undefined,
                  densityScalar === null && densityUnavailableReason ? unavailableCaption : undefined
                )}
          </StatValueCard>
        </Box>

        {/* No divider here: the chart's own tabs strip (full-bleed border-bottom)
            already separates it from the tiles above, so a second rule right
            on top of it was a redundant double line. */}
        {chart ? <Box sx={{ mt: 3 }}>{chart}</Box> : null}
      </CardContent>
    </Card>
  )
})

// Density scalar from a trends item's per-adopting-game series (its last month), the way
// every detail page reads it.
//
// The detail pages show no density change by choice (docs/reskins/2026-10-28/decision-density-change-column.md,
// "Not part of this decision"). The Ranked List does show one: last − first month, each over
// that month's adopters, so it mixes intensity with who adopts, and a tile here would need the
// same caveat. If the matched-cohort comparison in
// docs/reskins/2026-09-16/backend-handoff-density-change.md lands, it needs a slot of its own.
export function densityStatsOf(density: BlueprintFamilyTrendsMonthlyPoint[] | undefined): { densityScalar: number | null } {
  return { densityScalar: densityScalarOf({ density }) }
}
